import React from 'react';
import StringConstants from '@common/constants/StringConstants';
import errorMessages from '@common/constants/errorMessages';
import { Anchor, Box, Input, Label, Paragraph } from '@zillow/constellation-10';

const BACKSPACE_KEYCODE = 8;

type Props = {
  error: string;
  setVerificationCode: (val: string) => void;
};

type State = {
  digit1: string;
  digit2: string;
  digit3: string;
  digit4: string;
  digit5: string;
};

class PhoneVerificationInput extends React.Component<Props, State> {
  ref1 = React.createRef<HTMLInputElement>();
  ref2 = React.createRef<HTMLInputElement>();
  ref3 = React.createRef<HTMLInputElement>();
  ref4 = React.createRef<HTMLInputElement>();
  ref5 = React.createRef<HTMLInputElement>();

  static defaultProps = {
    error: '',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      digit1: '',
      digit2: '',
      digit3: '',
      digit4: '',
      digit5: '',
    };
  }

  setVerificationCode = (): void => {
    const { digit1, digit2, digit3, digit4, digit5 } = this.state;
    this.props.setVerificationCode(digit1 + digit2 + digit3 + digit4 + digit5);
  };

  updateInput = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;
    const digit = Number(e.target.dataset.digit);

    if (/^\d?$/.test(value)) {
      this.setState(
        { [`digit${digit}`]: value } as Pick<State, 'digit1' | 'digit2' | 'digit3' | 'digit4' | 'digit5'>,
        () => {
          this.setVerificationCode();
          this.focusNextIfPossible({ digit, value });
        },
      );
    }
  };

  focusNextIfPossible = ({ digit, value }): void => {
    if (value && digit < 5) {
      this[`ref${digit + 1}`].current?.focus();
    }
  };

  focusPreviousOnBackspace = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const digit = Number(e.currentTarget.dataset.digit);
    const value = this.state[`digit${digit}`];

    if (e.key === 'Backspace') {
      if (!value && digit > 1) {
        this[`ref${digit - 1}`].current?.focus();
      }
    }
  };

  renderErrorText = (error: string): JSX.Element | null => {
    const errorMessage =
      error === errorMessages.PHONE_VERIFICATION_TOO_MANY_ATTEMPTS ? (
        <>
          Too many attempts have been made. Please&nbsp;
          <Anchor target="_blank" href={StringConstants.LINKS.SUPPORT_PAGE.LANDLORD}>
            contact support
          </Anchor>
          &nbsp;to continue.
        </>
      ) : (
        error
      );

    return (
      <Paragraph
        textStyle="body-xs"
        css={{
          color: 'red.400',
        }}
      >
        {errorMessage}
      </Paragraph>
    );
  };

  render(): React.ReactNode {
    const { digit1, digit2, digit3, digit4, digit5 } = this.state;
    const { error } = this.props;

    return (
      <Box
        css={{
          display: 'flex',
          gap: 'token(spacing.xs) !important',
          padding: 'token(spacing.xs) !important',
          alignItems: 'center',
        }}
      >
        <Label>5-digit Code</Label>
        <Input
          data-digit="1"
          css={{ width: '50px !important', display: 'inline-block', textAlign: 'center' }}
          autoFocus
          ref={this.ref1}
          value={digit1}
          onChange={this.updateInput}
          onKeyDown={this.focusPreviousOnBackspace}
        />
        <Input
          data-digit="2"
          css={{ width: '50px !important', display: 'inline-block', textAlign: 'center' }}
          ref={this.ref2}
          value={digit2}
          onChange={this.updateInput}
          onKeyDown={this.focusPreviousOnBackspace}
        />
        <Input
          data-digit="3"
          css={{ width: '50px !important', display: 'inline-block', textAlign: 'center' }}
          ref={this.ref3}
          value={digit3}
          onChange={this.updateInput}
          onKeyDown={this.focusPreviousOnBackspace}
        />
        <Input
          data-digit="4"
          css={{ width: '50px !important', display: 'inline-block', textAlign: 'center' }}
          ref={this.ref4}
          value={digit4}
          onChange={this.updateInput}
          onKeyDown={this.focusPreviousOnBackspace}
        />
        <Input
          data-digit="5"
          css={{ width: '50px !important', display: 'inline-block', textAlign: 'center' }}
          ref={this.ref5}
          value={digit5}
          onChange={this.updateInput}
          onKeyDown={this.focusPreviousOnBackspace}
        />
        {error && this.renderErrorText(error)}
      </Box>
    );
  }
}

export default PhoneVerificationInput;
